import ContactPageIcon from '@mui/icons-material/ContactPage';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { ReactElement, ReactNode, useState } from 'react';
import logoSrc from '../../assets/logo-nazca-solutions.svg';
import IconButton, { IconButtonProps } from '../../components/IconButton';
import { useDialog } from '../../contexts/dialog/hook';
import ContactDialog from '../../dialogs/ContactDialog';

interface NavbarActionButtonProps {
  sx?: IconButtonProps['sx'];
}

function HelpButton({ sx }: NavbarActionButtonProps): ReactElement {
  const theme = useTheme();

  const isMobilleScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <IconButton
      icon={ContactSupportIcon}
      title="Open het Klantportaal"
      size={isMobilleScreen ? 'medium' : 'large'}
      sx={sx}
      href="https://klantportaal.nazca.solutions/"
      target="_blank"
    />
  );
}

function ContactButton({ sx }: NavbarActionButtonProps): ReactElement {
  const dialog = useDialog();

  const theme = useTheme();

  const isMobilleScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <IconButton
        icon={ContactPageIcon}
        title="Neem contact met ons op"
        size={isMobilleScreen ? 'medium' : 'large'}
        sx={sx}
        onClick={dialog.open}
      />
      <ContactDialog />
    </>
  );
}

export interface DefaultLayoutProps {
  children?: ReactNode;
}

export default function DefaultLayout({ children }: DefaultLayoutProps): ReactElement {
  const { spacing, breakpoints } = useTheme();

  const xSmallScreen = useMediaQuery(breakpoints.only('xs'));

  const [isElevated, setElevated] = useState(false);

  useScrollPosition(
    ({ currPos }) => {
      // If scrolled passed the <main /> top padding:
      const scrolledFarEnough = -currPos.y >= parseInt(spacing(xSmallScreen ? 2 : 3), 10);
      if (isElevated !== scrolledFarEnough) {
        setElevated(scrolledFarEnough);
      }
    },
    [isElevated]
  );

  return (
    <Container
      maxWidth={false}
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
      }}
    >
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 1,
          width: 1,
        }}
        square
      >
        <Paper
          component="nav"
          elevation={isElevated ? 7 : 0}
          square
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: { xs: 2, md: 5 },
            pb: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            position: 'sticky',
            justifyContent: 'flex-start',
            width: '100%',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            backgroundColor: ({ palette }) => palette.background.default,
            clipPath: 'inset(0px 0px -24px 0px)',
          }}
        >
          <Box
            component="img"
            src={logoSrc}
            alt="Logo van Nazca Solutions"
            sx={{
              width: 'auto',
              height: { xs: spacing(6), md: spacing(11) },
              display: 'block',
            }}
          />
          <HelpButton sx={{ ml: 'auto' }} />
          <ContactButton
            sx={{
              mr: {
                xs: `-${spacing(1.5)}`,
                md: `-${spacing(2.25)}`,
              },
            }}
          />
          {!isElevated && (
            <Divider sx={{ position: 'absolute', width: 1, bottom: 0, left: 0 }} light />
          )}
        </Paper>

        <Box
          component="main"
          sx={{
            height: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 1,
            p: { xs: 2, md: 5 },
            pt: { xs: 2, md: 3 },
          }}
        >
          {children}
        </Box>
      </Paper>
    </Container>
  );
}
